import { Component, OnInit } from '@angular/core';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { CardModule } from 'primeng/card';
import { firstValueFrom } from 'rxjs';
import { MessageModule } from 'primeng/message';
import { CognitoService } from '../../../../../services/cognito.service';
import { IUser } from '../../../../../models/i-user';
import { UserRoles } from '../../../../../models/user-roles.enum';

/**
 * Enum representing possible session sign-in flags.
 */
export enum SessionSignInFlag {
  /**
   * Flag indicating that sign-in is required.
   */
  SignInRequired = 'signin-required',
  /**
   * Flag indicating that a session is required.
   */
  SessionRequired = 'session-required',
}

/**
 * Component for handling session sign-in functionality.
 */
@Component({
  selector: 'app-besharp-session-signin',
  standalone: true,
  imports: [
    Button,
    ReactiveFormsModule,
    DividerModule,
    FloatLabelModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    CardModule,
    FormsModule,
    RouterModule,
    MessageModule,
  ],
  templateUrl: './session-signin.component.html',
  styleUrls: ['./session-signin.component.scss'],
})
export class SessionSigninComponent implements OnInit {
  /**
   * Enum for session sign-in flags.
   */
  protected eSessionSignInFlag = SessionSignInFlag;

  /**
   * Error message to display.
   */
  protected errorMessage: string | null = null;

  /**
   * Mapping of session sign-in flags to their corresponding labels.
   */
  protected flagLabelMapping = {
    [SessionSignInFlag.SessionRequired]: 'No active session, please sign-in.',
    [SessionSignInFlag.SignInRequired]: 'Session expired, please sign-in again.',
  };

  /**
   * Form group for handling form data.
   */
  protected formGroup: FormGroup = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  /**
   * Flag indicating whether data is being loaded.
   */
  protected loading: boolean = false;

  /**
   * Flag indicating whether credentials should be saved in the configured storage.
   */
  protected saveCredentials: boolean = false;

  /**
   * List of session sign-in flags.
   */
  protected flags: SessionSignInFlag[] = [];

  /**
   * Constructor for the component.
   */
  public constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    private cognitoService: CognitoService
  ) {}

  /**
   * Initializes the component.
   */
  async ngOnInit(): Promise<void> {
    const queryParams = await firstValueFrom(this.route.queryParamMap);
    this.flags = Object.values(SessionSignInFlag).filter((flag) =>
      queryParams.get('flags')?.split(/(,|;)/).includes(flag)
    );
  }

  /**
   * Checks if the form is valid.
   *
   * @returns True if the form is valid, false otherwise.
   */
  protected isValid(): boolean {
    return this.formGroup.valid;
  }

  /**
   * Handles changes to the save credentials checkbox.
   *
   * @param checked Whether credentials should be saved.
   */
  protected onChangeSaveCredentials(checked: boolean) {
    this.saveCredentials = checked;
  }

  /**
   * Navigates to the signup page.
   */
  protected goToSignup() {
    this.router.navigate(['/signup']);
  }

  /**
   * Starts a new session.
   *
   * @param event Event that triggered the session start.
   */
  protected async startSession(event: any) {
    event.preventDefault();
    this.errorMessage = null;
    const authData = this.formGroup.getRawValue();
    this.loading = true;
    try {
      try {
        const isAuthenticated = await this.cognitoService.isAuthenticated();
        if (isAuthenticated) {
          await this.cognitoService.signOut();
        }
      } catch (logoutError) {}

      const user: IUser = {
        email: authData.username,
        password: authData.password,
        name: authData.username
      };

      const result = await this.cognitoService.signIn(user);
      
      if (result.action === 'NEW_PASSWORD_REQUIRED') {
        this.errorMessage = 'New password required. Please contact administrator.';
      } else if (result.action === 'SIGNED_IN') {
        const currentUser = result.user as IUser;
        if (currentUser.role === UserRoles.PENDING_USER) {
          this.router.navigate(['/registration-submitted']);
        } else {
          this.router.navigate(['/app']);
        }
      }
    } catch (error: any) {
      if (error.code === 'NotAuthorizedException') {
        this.errorMessage = 'Invalid username or password';
      } else if (error.code === 'UserNotFoundException') {
        this.errorMessage = 'User not found';
      } else if (error.code === 'UserNotConfirmedException') {
        this.errorMessage = 'User not confirmed. Please check your email for confirmation instructions.';
      } else {
        this.errorMessage = 'Authentication failed. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }
}
